import React from 'react';
import { useTranslation } from 'react-i18next';
import LifecycleTypeTab from '../../../Employees/LifecycleSettings/components/LifecycleTypeTab';
import {
  getCategories,
  addCategory,
  updateCategory,
  deleteCategory,
  BusinessRoleCategoryInput,
} from '../../../../../api/General/businessRoleSettings';
import type { Result } from '../../../../../models/general';

// Categories are name/isActive only; the generic tab expects a companyId field, so we backfill it.
interface CategoryTabItem {
  id: number;
  name: string;
  isActive: boolean;
  companyId: number;
}

const withCompanyId = <T extends { id: number; name: string; isActive: boolean }>(item: T): CategoryTabItem => ({
  id: item.id,
  name: item.name,
  isActive: item.isActive,
  companyId: 0,
});

const CategoriesTab: React.FC = () => {
  const { t } = useTranslation();

  const fetchItems = async (): Promise<Result<CategoryTabItem[]>> => {
    const res = await getCategories();
    return { ...res, payload: (res.payload ?? []).map(withCompanyId) };
  };

  const addItem = async (input: BusinessRoleCategoryInput): Promise<Result<CategoryTabItem>> => {
    const res = await addCategory(input);
    return { ...res, payload: res.payload ? withCompanyId(res.payload) : (res.payload as unknown as CategoryTabItem) };
  };

  const updateItem = async (id: number, input: BusinessRoleCategoryInput): Promise<Result<CategoryTabItem>> => {
    const res = await updateCategory({ ...input, id });
    return { ...res, payload: res.payload ? withCompanyId(res.payload) : (res.payload as unknown as CategoryTabItem) };
  };

  return (
    <LifecycleTypeTab
      title={t('settings.roleCategories.title')}
      description={t('settings.roleCategories.description')}
      emptyMessage={t('settings.roleCategories.empty')}
      emptyIcon="Tags"
      addLabel={t('settings.roleCategories.add')}
      editLabel={t('settings.roleCategories.edit')}
      deleteLabel={t('settings.roleCategories.delete')}
      nameLabel={t('settings.roleCategories.nameLabel')}
      namePlaceholder={t('settings.roleCategories.namePlaceholder')}
      successAddMessage={t('settings.roleCategories.addSuccess')}
      successUpdateMessage={t('settings.roleCategories.updateSuccess')}
      successDeleteMessage={t('settings.roleCategories.deleteSuccess')}
      failAddMessage={t('settings.roleCategories.saveFail')}
      failDeleteMessage={t('settings.roleCategories.deleteFail')}
      deleteConfirmMessage={t('settings.roleCategories.deleteConfirm')}
      duplicateMessage={t('settings.roleCategories.duplicate')}
      fetchItems={fetchItems}
      addItem={addItem}
      updateItem={updateItem}
      deleteItem={deleteCategory}
      testIdPrefix="role-category"
    />
  );
};

export default CategoriesTab;
